import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Input from "../../../../components/Form/Input";
import Button from "../../../../components/Form/Button";
import Select from "../../../../components/Form/Select";
import Modal from "../../../../components/Modal";
import { getTenant } from "../Tenants.slice";
import { pushToast } from "../../../../components/Toaster/Toaster.slice";
import { postUtilityPaymentApi, postCorpPaymentApi, postUtilityDepositPaymentApi, postManualUtilityPaymentApi } from "../../../../apis/apis";
import { ServiceAccount } from "../../../../apis/types";
import { useAppDispatch, useAppSelector } from "../../../../shared/redux/hooks";

export interface PostUtilityPaymentFormProps {
  serviceAccount: ServiceAccount
  onSuccess?: () => void
  onCancel?: () => void
}

export const PostUtilityPaymentForm: React.FC<PostUtilityPaymentFormProps> = (props) => {
  const tenantID = useAppSelector((state) => state.tenants.tenant?.id)
  const dispatch = useAppDispatch()
  const { serviceAccount, onSuccess, onCancel } = props
  const { permissions } = useAppSelector((state) => state.app)
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
  const [submittingType, setSubmittingType] = useState<string | null>(null)

  const paymentTypeOptions = {
    '': 'Select payment type',
    'CCA': 'CCA Refund',
    'UTILITY': 'Utility Refund',
    'CORP': 'Tenant Refund',
    'DEPOSIT': 'Utility Payment Deposit',
    'CORP_UTILITY_PAYMENT': 'Corp Utility Payment',
    'TENANT_UTILITY_PAYMENT': 'Tenant Utility Payment'
  }

  const hasTakePaymentPermission = (): boolean => (permissions != null ? permissions.includes('payops:take:payment') : false)

  const d = new Date()
  const defaultDate = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`

  const handleUtilityPayment = async (values: any, paymentType: 'CCA' | 'UTILITY', resetForm: () => void) => {
    // De-duplication check - prevent rapid submissions
    if (submittingType === paymentType) {
      dispatch(pushToast({
        type: 'error',
        message: 'Submission in progress',
        description: 'Please wait for the current submission to complete.'
      }))
      return
    }

    const [month, day, year] = values.paymentDate.split('/')
    const paymentDate = new Date(Number(year), Number(month) - 1, Number(day)).getTime()

    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      
      setSubmittingType(paymentType)
      
      try {
        await postUtilityPaymentApi({
          amount: Number(values.amount),
          paymentDate,
          memo: values.memo,
          paymentType,
          userId: serviceAccount.accountManagers[0].id, // Use first account manager as default
          tenantAccountCuid: serviceAccount.id
        })
        
        dispatch(pushToast({ 
          message: `${paymentType} refund of $${values.amount} posted successfully and recorded as revenue` 
        }))

        // Clear form fields on successful submission
        resetForm()

        if (tenantID !== undefined) {
          dispatch(getTenant(tenantID))
        }
        onSuccess?.()
      } catch (e: any) {
        dispatch(
          pushToast({
            type: 'error',
            message: `Error posting ${paymentType} refund`,
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      } finally {
        setSubmittingType(null)
      }
    } catch (e) {
      setConfirmModal(false)
      setSubmittingType(null)
    }
  }

  const handleCorpPayment = async (values: any, resetForm: () => void) => {
    // De-duplication check - prevent rapid submissions
    if (submittingType === 'CORP') {
      dispatch(pushToast({
        type: 'error',
        message: 'Submission in progress',
        description: 'Please wait for the current submission to complete.'
      }))
      return
    }

    const [month, day, year] = values.paymentDate.split('/')
    const paymentDate = new Date(Number(year), Number(month) - 1, Number(day)).getTime()

    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      
      setSubmittingType('CORP')
      
      try {
        await postCorpPaymentApi({
          amount: Number(values.amount),
          paymentDate,
          memo: values.memo,
          userId: serviceAccount.accountManagers[0].id, // Use first account manager as default
          tenantAccountCuid: serviceAccount.id
        })
        
        dispatch(pushToast({ 
          message: `Tenant refund of $${values.amount} posted successfully and recorded` 
        }))

        // Clear form fields on successful submission
        resetForm()

        if (tenantID !== undefined) {
          dispatch(getTenant(tenantID))
        }
        onSuccess?.()
      } catch (e: any) {
        dispatch(
          pushToast({
            type: 'error',
            message: `Error posting tenant refund`,
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      } finally {
        setSubmittingType(null)
      }
    } catch (e) {
      setConfirmModal(false)
      setSubmittingType(null)
    }
  }

  const handleUtilityDepositPayment = async (values: any, resetForm: () => void) => {
    // De-duplication check - prevent rapid submissions
    if (submittingType === 'DEPOSIT') {
      dispatch(pushToast({
        type: 'error',
        message: 'Submission in progress',
        description: 'Please wait for the current submission to complete.'
      }))
      return
    }

    const [month, day, year] = values.paymentDate.split('/')
    const paymentDate = new Date(Number(year), Number(month) - 1, Number(day)).getTime()

    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      
      setSubmittingType('DEPOSIT')
      
      try {
        await postUtilityDepositPaymentApi({
          amount: Number(values.amount),
          paymentDate,
          memo: values.memo,
          userId: serviceAccount.accountManagers[0].id, // Use first account manager as default
          tenantAccountCuid: serviceAccount.id
        })
        
        dispatch(pushToast({ 
          message: `Utility deposit of $${values.amount} posted successfully and recorded` 
        }))

        // Clear form fields on successful submission
        resetForm()

        if (tenantID !== undefined) {
          dispatch(getTenant(tenantID))
        }
        onSuccess?.()
      } catch (e: any) {
        dispatch(
          pushToast({
            type: 'error',
            message: `Error posting utility deposit payment`,
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      } finally {
        setSubmittingType(null)
      }
    } catch (e) {
      setConfirmModal(false)
      setSubmittingType(null)
    }
  }

  const handleManualUtilityPayment = async (values: any, paymentType: 'CORP_UTILITY_PAYMENT' | 'TENANT_UTILITY_PAYMENT', resetForm: () => void) => {
    // De-duplication check - prevent rapid submissions
    if (submittingType === paymentType) {
      dispatch(pushToast({
        type: 'error',
        message: 'Submission in progress',
        description: 'Please wait for the current submission to complete.'
      }))
      return
    }

    const [month, day, year] = values.paymentDate.split('/')
    const paymentDate = new Date(Number(year), Number(month) - 1, Number(day)).getTime()

    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)

      setSubmittingType(paymentType)

      try {
        await postManualUtilityPaymentApi({
          amount: Number(values.amount),
          paymentDate,
          memo: values.memo,
          paymentType,
          userId: serviceAccount.accountManagers[0].id, // Use first account manager as default
          tenantAccountCuid: serviceAccount.id
        })

        const paymentTypeName = paymentType === 'CORP_UTILITY_PAYMENT' ? 'Corp utility payment' : 'Tenant utility payment'
        dispatch(pushToast({
          message: `${paymentTypeName} of $${values.amount} posted successfully`
        }))

        // Clear form fields on successful submission
        resetForm()

        if (tenantID !== undefined) {
          dispatch(getTenant(tenantID))
        }
        onSuccess?.()
      } catch (e: any) {
        const paymentTypeName = paymentType === 'CORP_UTILITY_PAYMENT' ? 'corp utility payment' : 'tenant utility payment'
        dispatch(
          pushToast({
            type: 'error',
            message: `Error posting ${paymentTypeName}`,
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      } finally {
        setSubmittingType(null)
      }
    } catch (e) {
      setConfirmModal(false)
      setSubmittingType(null)
    }
  }

  const handleSubmit = async (values: any, resetForm: () => void) => {
    const { paymentType } = values

    switch (paymentType) {
      case 'CCA':
      case 'UTILITY':
        await handleUtilityPayment(values, paymentType as 'CCA' | 'UTILITY', resetForm)
        break
      case 'CORP':
        await handleCorpPayment(values, resetForm)
        break
      case 'DEPOSIT':
        await handleUtilityDepositPayment(values, resetForm)
        break
      case 'CORP_UTILITY_PAYMENT':
      case 'TENANT_UTILITY_PAYMENT':
        await handleManualUtilityPayment(values, paymentType as 'CORP_UTILITY_PAYMENT' | 'TENANT_UTILITY_PAYMENT', resetForm)
        break
      default:
        dispatch(pushToast({
          type: 'error',
          message: 'Please select a payment type'
        }))
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          amount: '',
          paymentDate: defaultDate,
          memo: '',
          paymentType: '',
        }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          amount: Yup.string()
            .required('Required field')
            .test('num', 'Must be a number', (value) => !isNaN(Number(value)))
            .test('min', 'Must be greater than zero', (value) => Number(value) > 0)
            .matches(/^[0-9]+(.[0-9]{2})?$/, 'Please enter whole dollars and cents (e.g. 100.00)'),
          paymentDate: Yup.string()
            .required('Required field')
            .matches(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/, 'M/D/YYYY format required'),
          paymentType: Yup.string().required('Required field').notOneOf([''], 'Please select a payment type'),
        })}
        onSubmit={() => {}}
      >
        {({ values, errors, touched, isValid, resetForm }) => (
          <>
            <Form>
              <div className="grid grid-cols-1 gap-3">
                <Field name="paymentType">
                  {({ field, form }: { field: any; form: any }) => (
                    <Select field={field} form={form} label="Payment Type" options={paymentTypeOptions} required disabled={!hasTakePaymentPermission()} />
                  )}
                </Field>

                <Field
                  as={Input}
                  name="amount"
                  label="Amount"
                  error={errors.amount !== undefined && (touched.amount ?? false) ? errors.amount : undefined}
                  disabled={!hasTakePaymentPermission()}
                />

                <Field
                  as={Input}
                  name="paymentDate"
                  label="Payment date"
                  error={errors.paymentDate !== undefined && (touched.paymentDate ?? false) ? errors.paymentDate : undefined}
                  disabled={!hasTakePaymentPermission()}
                />

                <Field
                  as={Input}
                  name="memo"
                  label="Memo (optional)"
                  error={errors.memo !== undefined && (touched.memo ?? false) ? errors.memo : undefined}
                  disabled={!hasTakePaymentPermission()}
                />
              </div>

              {hasTakePaymentPermission() && (
                <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  {onCancel != null && (
                    <Button type="button" color="slate" onClick={onCancel} disabled={submittingType !== null}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    disabled={!isValid || !hasTakePaymentPermission() || submittingType !== null || values.paymentType === ''}
                    onClick={() => handleSubmit(values, resetForm)}
                  >
                    {submittingType !== null ? 'Processing...' : 'Submit Payment'}
                  </Button>
                </div>
              )}
              {!hasTakePaymentPermission() && onCancel != null && (
                <div className="mt-4 flex justify-end">
                  <Button type="button" color="slate" onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
              )}
            </Form>
            {confirmModal}
          </>
        )}
      </Formik>
    </>
  )
}

export default PostUtilityPaymentForm;